import { Map } from 'immutable';
import { omitBy } from 'lodash';
import { EntityTypesEnum } from '../../../app.enum';
import { hasValue, IChangeField } from '../../../utils/helpers/model.helper';
import { ITimeEntry } from '../../appointment-data/appointment-data.data';
import { DocumentPreview } from '../../document-data/sub-models/document-preview.model';
import {
  entityKeys,
  entityModelFactory,
  generateContent,
} from '../../entity-data/entity.model';
import { IModelValidator } from '../../shared/interfaces/model-validator.interface';
import { BilledDocument } from './billed-document.model';
import { ClientPreview } from './client-preview.model';
import { ClientReference } from './client-reference.model';

export const PARENT_SCHEMA = Object.freeze({
  METADATA: null,
  CLIENT_PREVIEW: null, // ClientPreview
  BILLED_DOCUMENT: null, // BilledDocument
  BILLED_DOCUMENT_PREVIEW: null, // DocumentPreview
});
export const CONTENT_SCHEMA = Object.freeze({
  CLIENT: null, // ClientReference
  START_DATE: null, // i2g-date-time (yyyy-mm-dd'T'hh:mm:ssZ)
  DURATION: 0, // seconds
  NOTES: '',
  LOCALE: '',
});
const KEYS = entityKeys(PARENT_SCHEMA, CONTENT_SCHEMA);
export const CONTENT_KEYS = generateContent(CONTENT_SCHEMA).KEYS;
const parentChanges = (props: Map<string, any>): IChangeField[] => {
  const clientPreview = props.get(KEYS.CLIENT_PREVIEW);
  const billedDocument = props.get(KEYS.BILLED_DOCUMENT);
  const billedDocumentPreview = props.get(KEYS.BILLED_DOCUMENT_PREVIEW);

  return [
    {
      key: KEYS.CLIENT_PREVIEW,
      value: clientPreview ? new ClientPreview(clientPreview) : null,
    },
    {
      key: KEYS.BILLED_DOCUMENT,
      value: billedDocument ? new BilledDocument(billedDocument) : null,
    },
    {
      key: KEYS.BILLED_DOCUMENT_PREVIEW,
      value: billedDocumentPreview
        ? new DocumentPreview(billedDocumentPreview)
        : null,
    },
  ];
};
const contentChanges = (contentProps: Map<string, any>): IChangeField[] => {
  const client = contentProps.get(CONTENT_KEYS.CLIENT);

  return [
    {
      key: CONTENT_KEYS.CLIENT,
      value: client ? new ClientReference(client) : null,
    },
  ];
};

export class TrackedTime
  extends entityModelFactory(
    PARENT_SCHEMA,
    parentChanges,
    CONTENT_SCHEMA,
    contentChanges
  )
  implements IModelValidator, ITimeEntry {
  static ENTITY_TYPE: EntityTypesEnum = EntityTypesEnum.tracked_time;
  static KEYS: any = KEYS;
  static CONTENT_KEYS: any = CONTENT_KEYS;

  get ENTITY_TYPE(): string {
    return TrackedTime.ENTITY_TYPE;
  }

  get billingName(): string {
    return this.clientPreview ? this.clientPreview.billingName : null;
  }

  get clientPreview(): ClientPreview {
    return this.get(KEYS.CLIENT_PREVIEW);
  }

  get billedDocument(): BilledDocument {
    return this.get(KEYS.BILLED_DOCUMENT);
  }

  get billedDocumentPreview(): DocumentPreview {
    return this.get(KEYS.BILLED_DOCUMENT_PREVIEW);
  }

  get clientReference(): ClientReference {
    return this.getFieldInContent(CONTENT_KEYS.CLIENT);
  }

  get startDate(): string {
    return this.getFieldInContent(CONTENT_KEYS.START_DATE);
  }

  setStartDate(startDate: string): TrackedTime {
    return this.setFieldInContent(
      CONTENT_KEYS.START_DATE,
      startDate
    ) as TrackedTime;
  }

  get duration(): number {
    return this.getFieldInContent(CONTENT_KEYS.DURATION);
  }

  get notes(): string {
    return this.getFieldInContent(CONTENT_KEYS.NOTES);
  }

  get locale(): string {
    return this.getFieldInContent(CONTENT_KEYS.LOCALE);
  }

  hasValidClient(): boolean {
    return this.clientPreview && !this.clientPreview.isDeleted();
  }

  hasValue(): boolean {
    const hasFieldsPopulated = hasValue(
      KEYS,
      omitBy(
        CONTENT_SCHEMA,
        (_, key) => key.toLowerCase() === CONTENT_KEYS.CLIENT.toLowerCase()
      ),
      this.content.toMap()
    );

    return (
      hasFieldsPopulated ||
      (!!this.clientReference && this.clientReference.hasValue())
    );
  }
}
